import { isNodeSelection } from '@tiptap/core';
import type { Selection } from '@tiptap/pm/state';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReplaceStep } from '@tiptap/pm/transform';

import type { ExtensionProps, RicosExtension, RicosExtensionConfig } from './../../ricos-types';
import { createRicosFunctionalExtension } from './../../plugin-commons';

export const notOvertypable = createRicosFunctionalExtension({
  type: 'extension',
  groups: [],
  name: 'ricos-not-overtypable',
  reconfigure(
    config: RicosExtensionConfig,
    extensions: RicosExtension[],
    _ricosProps: ExtensionProps
  ) {
    const types = extensions
      .filter(extension => extension.groups.includes('not-overtypable'))
      .map(({ name }) => name);

    const getIsTypingDisabled = (selection: Selection) =>
      isNodeSelection(selection) && types.includes(selection.node.type.name);

    return {
      ...config,
      priority: 70,
      addProseMirrorPlugins() {
        return [
          new Plugin({
            key: new PluginKey('ricos-not-overtypable'),
            /**
             * This place sets the disableTyping state based on the current selection.
             * init is called on initialization and apply is called on every transaction.
             */
            state: {
              init(_, { selection }) {
                return {
                  disableTyping: getIsTypingDisabled(selection),
                };
              },
              apply(_tr, _value, _oldState, { selection }) {
                return {
                  disableTyping: getIsTypingDisabled(selection),
                };
              },
            },
            /**
             * This place filters transactions that are not allowed to change the document.
             * If doc is not changed or disableTyping is false, the transaction is allowed.
             * If doc is changed and disableTyping is true, the transaction is allowed only if it
             * DOESNT replace a node with text.
             *
             * This is done to prevent overtyping nodes that are not supposed to be overtyped. (e.g. images, polls, etc.)
             * It means that when you have a node selected that has this plugin,
             * and you start typing, the text wont be replaced instead of the node.
             */
            filterTransaction(tr, state) {
              const { disableTyping } = this.getState(state);
              const allowTextReplace = tr.getMeta('allowTextReplace');

              if (!tr.docChanged || !disableTyping || allowTextReplace) {
                return true;
              }
              // Check if one of the steps is replacing node with disabled typing
              // with TEXT.
              const isReplacingWithText = tr.steps.some(step => {
                let isText = false;
                if (step instanceof ReplaceStep) {
                  step.slice.content.descendants(node => {
                    if (node.isText) {
                      isText = true;
                      // Return false so we do not go into other descendants.
                      return false;
                    }
                    // If we already found that it is text, we can return true
                    return !isText;
                  });
                }

                return isText;
              });

              return !isReplacingWithText;
            },
          }),
        ];
      },
    };
  },
  createExtensionConfig() {
    return {
      name: this.name,
    };
  },
});
